import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import { CheckCircle2, Eye, ImagePlus, PenSquare, Send, UploadCloud, X } from 'lucide-react';
import CustomSelect from './ui/CustomSelect';
import BlogNavbar from './BlogNavbar';
import { useAuth } from '../contexts/AuthContext';
import {
  createMyBlog,
  DEFAULT_BLOG_CATEGORY_OPTIONS,
  fetchMyBlog,
  submitMyBlog,
  uploadBlogCoverToCloudinary,
  updateMyBlog
} from '../services/blogService';
import 'react-quill/dist/quill.snow.css';
import './WriteBlogPage.css';

const BLOG_DRAFT_STORAGE_KEY = 'race-next-step-blog-draft';
const BLOG_DRAFT_ID_STORAGE_KEY = 'race-next-step-blog-draft-id';

const initialForm = {
  category: 'College Guide',
  title: '',
  excerpt: '',
  content: '',
  readTime: '',
  tags: '',
  authorName: ''
};

const toPlainText = (html = '') =>
  String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const WriteBlogPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const quillRef = React.useRef(null);
  const previewRef = React.useRef(null);
  const [form, setForm] = useState(initialForm);
  const [coverPreview, setCoverPreview] = useState('');
  const [coverName, setCoverName] = useState('');
  const [coverImage, setCoverImage] = useState({});
  const [editorStatus, setEditorStatus] = useState('Draft in progress');
  const [statusTone, setStatusTone] = useState('draft');
  const [blogId, setBlogId] = useState('');
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const draftIdFromQuery = searchParams.get('blogId');

  useEffect(() => {
    const fallbackAuthorName = user?.displayName || user?.email || '';
    const savedDraft = window.localStorage.getItem(BLOG_DRAFT_STORAGE_KEY);
    const storedDraftId = window.localStorage.getItem(BLOG_DRAFT_ID_STORAGE_KEY);

    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        if (parsedDraft?.form) {
          setForm((current) => ({ ...current, ...parsedDraft.form, authorName: parsedDraft.form.authorName || fallbackAuthorName }));
        } else {
          setForm((current) => ({ ...current, authorName: fallbackAuthorName }));
        }
        if (parsedDraft?.status) {
          setEditorStatus(parsedDraft.status);
        }
        if (parsedDraft?.tone) {
          setStatusTone(parsedDraft.tone);
        }
        if (parsedDraft?.coverName) {
          setCoverName(parsedDraft.coverName);
        }
      } catch (error) {
        window.localStorage.removeItem(BLOG_DRAFT_STORAGE_KEY);
      }
    } else {
      setForm((current) => ({ ...current, authorName: fallbackAuthorName }));
    }

    if (storedDraftId && !draftIdFromQuery) {
      setBlogId(storedDraftId);
    }
  }, [draftIdFromQuery, user?.displayName, user?.email]);

  useEffect(() => {
    let isMounted = true;
    const activeBlogId = draftIdFromQuery || window.localStorage.getItem(BLOG_DRAFT_ID_STORAGE_KEY) || '';

    const loadDraft = async () => {
      if (!activeBlogId) {
        setLoadingDraft(false);
        return;
      }

      try {
        const draft = await fetchMyBlog(activeBlogId);
        if (!isMounted) {
          return;
        }

        setBlogId(draft._id || activeBlogId);
        window.localStorage.setItem(BLOG_DRAFT_ID_STORAGE_KEY, draft._id || activeBlogId);

        // Agar blog already submitted/published hai toh editor clear karo
        if (draft?.status === 'pending_approval' || draft?.status === 'published') {
          window.localStorage.removeItem(BLOG_DRAFT_STORAGE_KEY);
          window.localStorage.removeItem(BLOG_DRAFT_ID_STORAGE_KEY);
          setBlogId('');
          setEditorStatus(
            draft?.status === 'pending_approval'
              ? 'Your blog is under review. Start a new blog below.'
              : 'Your blog is published. Start a new blog below.'
          );
          setStatusTone('pending');
          setLoadingDraft(false);
          return;
        }
        setForm((current) => ({
          ...current,
          category: draft?.category?.label || current.category,
          title: draft?.title || '',
          excerpt: draft?.excerpt || '',
          content: draft?.content?.html || '',
          readTime: draft?.readTimeMinutes ? `${draft.readTimeMinutes} min` : current.readTime,
          tags: Array.isArray(draft?.tags) ? draft.tags.join(', ') : '',
          authorName: draft?.author?.fullName || current.authorName
        }));
        setCoverImage(draft?.coverImage || {});
        setCoverPreview(draft?.coverImage?.secureUrl || draft?.coverImage?.url || '');
        setCoverName(draft?.coverImage?.originalFileName || '');
        setEditorStatus(
          draft?.status === 'pending_approval'
            ? 'Submitted for admin approval'
            : draft?.status === 'rejected'
              ? 'This draft needs revision before resubmission'
              : 'Draft loaded from your workspace'
        );
        setStatusTone(
          draft?.status === 'pending_approval'
            ? 'pending'
            : draft?.status === 'rejected'
              ? 'warning'
              : 'success'
        );
      } catch (error) {
        if (isMounted) {
          setEditorStatus(error.message || 'Unable to load saved draft');
          setStatusTone('warning');
        }
      } finally {
        if (isMounted) {
          setLoadingDraft(false);
        }
      }
    };

    loadDraft();

    return () => {
      isMounted = false;
    };
  }, [draftIdFromQuery]);

  useEffect(() => {
    return () => {
      if (coverPreview) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    const handleImageClick = (event) => {
      const img = event.target;
      if (img.tagName !== 'IMG') return;

      const confirmDelete = window.confirm('Remove this image from the blog?');
      if (!confirmDelete) return;

      const range = editor.getSelection(true);
      const [blot, offset] = editor.getLeaf(range?.index ?? 0);

      // Find image blot and delete it
      const allBlots = editor.scroll.descendants(
        (b) => b.domNode === img,
        0,
        editor.getLength()
      );

      if (allBlots.length > 0) {
        const index = editor.getIndex(allBlots[0]);
        editor.deleteText(index, 1, 'user');
      } else if (blot?.domNode === img) {
        const index = editor.getIndex(blot) - offset;
        editor.deleteText(index, 1, 'user');
      }
    };

    const editorDom = editor.root;
    editorDom.addEventListener('click', handleImageClick);

    return () => {
      editorDom.removeEventListener('click', handleImageClick);
    };
  }, []);

  const selectedTags = useMemo(
    () =>
      form.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
        .slice(0, 5),
    [form.tags]
  );

  const autoReadTime = useMemo(() => {
    const wordCount = toPlainText(form.content).split(/\s+/).filter(Boolean).length;
    return `${Math.max(1, Math.ceil(wordCount / 200))} min`;
  }, [form.content]);

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean']
      ]
    }),
    []
  );

  const quillFormats = useMemo(
    () => ['header', 'bold', 'italic', 'underline', 'blockquote', 'list', 'bullet', 'link', 'image'],
    []
  );

  const previewContent = useMemo(() => {
    const trimmedContent = form.content?.trim();
    return trimmedContent ? trimmedContent : '<p>Your formatted blog content preview will appear here.</p>';
  }, [form.content]);
  const previewCoverSrc = coverPreview || coverImage?.secureUrl || coverImage?.url || '';

  const saveBlogState = (nextStatus, nextTone) => {
    window.localStorage.setItem(
      BLOG_DRAFT_STORAGE_KEY,
      JSON.stringify({
        form,
        status: nextStatus,
        tone: nextTone,
        coverName,
        coverImage
      })
    );
    setEditorStatus(nextStatus);
    setStatusTone(nextTone);
  };

  const buildBlogPayload = () => ({
    title: form.title,
    excerpt: form.excerpt,
    category: form.category,
    tags: form.tags,
    coverImage,
    content: {
      html: form.content,
      plainText: toPlainText(form.content)
    }
  });

  const resetEditor = () => {
    setForm({ ...initialForm, authorName: user?.displayName || user?.email || '' });
    setCoverPreview('');
    setCoverName('');
    setCoverImage({});
    setBlogId('');
    window.localStorage.removeItem(BLOG_DRAFT_STORAGE_KEY);
    window.localStorage.removeItem(BLOG_DRAFT_ID_STORAGE_KEY);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    saveBlogState('Saving draft...', 'preview');

    try {
      const payload = buildBlogPayload();
      const savedBlog = blogId
        ? await updateMyBlog(blogId, payload)
        : await createMyBlog(payload);

      if (savedBlog?._id) {
        setBlogId(savedBlog._id);
        window.localStorage.setItem(BLOG_DRAFT_ID_STORAGE_KEY, savedBlog._id);
      }

      setEditorStatus('Draft saved successfully');
      setStatusTone('success');
      setTimeout(() => resetEditor(), 1500);
    } catch (error) {
      setEditorStatus(error.message || 'Unable to save draft right now');
      setStatusTone('warning');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPost = () => {
    setEditorStatus('Live preview refreshed');
    setStatusTone('preview');
    previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePublishForApproval = async () => {
    const plainTextContent = form.content.replace(/<(.|\n)*?>/g, '').trim();
    const hasRequiredFields = form.title.trim() && form.excerpt.trim() && plainTextContent;

    if (!hasRequiredFields) {
      setEditorStatus('Add title, excerpt, and content before sending for approval');
      setStatusTone('warning');
      return;
    }

    setSaving(true);
    try {
      const payload = buildBlogPayload();
      const savedBlog = blogId
        ? await updateMyBlog(blogId, payload)
        : await createMyBlog(payload);

      const targetBlogId = savedBlog?._id || blogId;
      if (!targetBlogId) {
        throw new Error('Unable to determine draft id for submission');
      }

      await submitMyBlog(targetBlogId);
      setBlogId(targetBlogId);
      setEditorStatus('Submitted for admin approval');
      setStatusTone('pending');
      window.localStorage.removeItem(BLOG_DRAFT_STORAGE_KEY);
      window.localStorage.removeItem(BLOG_DRAFT_ID_STORAGE_KEY);
      setTimeout(() => resetEditor(), 1500);
    } catch (error) {
      setEditorStatus(error.message || 'Unable to submit blog for approval');
      setStatusTone('warning');
    } finally {
      setSaving(false);
    }
  };

  const statusClasses =
    statusTone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : statusTone === 'pending'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : statusTone === 'warning'
          ? 'border-rose-200 bg-rose-50 text-rose-700'
          : statusTone === 'preview'
            ? 'border-sky-200 bg-sky-50 text-sky-700'
            : 'border-[#eadcf8] bg-[#faf7ff] text-[#7d57b1]';

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleCoverChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const previousCoverPreview = coverPreview;
    const previousCoverName = coverName;
    const previousCoverImage = coverImage;

    if (coverPreview && coverPreview.startsWith('blob:')) {
      URL.revokeObjectURL(coverPreview);
    }

    const previewUrl = URL.createObjectURL(file);
    setCoverPreview(previewUrl);
    setCoverName(file.name);
    setUploadingCover(true);
    setEditorStatus('Uploading cover image...');
    setStatusTone('preview');

    try {
      const uploadedCover = await uploadBlogCoverToCloudinary(file);
      setCoverImage((current) => ({
        ...current,
        ...uploadedCover,
        altText: current?.altText || form.title || uploadedCover.originalFileName || ''
      }));
      setEditorStatus('Cover image uploaded successfully');
      setStatusTone('success');
    } catch (error) {
      if (previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      setCoverPreview(previousCoverPreview);
      setCoverName(previousCoverName);
      setCoverImage(previousCoverImage);
      setEditorStatus(error.message || 'Unable to upload cover image');
      setStatusTone('warning');
    } finally {
      setUploadingCover(false);
      event.target.value = '';
    }
  };

  const clearCover = () => {
    if (coverPreview && coverPreview.startsWith('blob:')) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverPreview('');
    setCoverName('');
    setCoverImage({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-[#1f1730]">
      <BlogNavbar
        title="Write a Blog"
        subtitle="Draft your title, upload a cover, and shape your story"
      />

      <section className="mx-auto max-w-7xl px-3 pb-8 sm:px-4 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">

          {/* Quick Notes - mobile only, shows above editor */}
          <div className="rounded-[30px] border border-gray-100 bg-white p-6 shadow-sm lg:hidden">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9a51a0]">Quick Notes</p>
            <ul className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
              <li>Choose a clear category so related blogs can be suggested correctly.</li>
              <li>Keep your title specific and reader-friendly.</li>
              <li>Upload a strong cover image because it appears in cards and detail pages.</li>
              <li>Publish sends the post into a pending state so an admin can review and approve it before it goes live.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:rounded-[30px] sm:p-6 lg:p-8">
            <div className="mb-4 sm:mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9a51a0]">Editor</p>
              <h2 className="mt-1.5 text-xl font-extrabold text-[#20192d] sm:mt-2 sm:text-3xl">Create Your Blog Post</h2>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                Save drafts, refine them, and submit for admin approval when ready.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-[#241b37]">Category</span>
                <CustomSelect
                  id="blog-category"
                  name="category"
                  options={DEFAULT_BLOG_CATEGORY_OPTIONS}
                  value={form.category}
                  onChange={handleInputChange}
                  placeholder="Select Category"
                  buttonClassName="px-4 py-3 border-gray-200 bg-white"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-[#241b37]">Estimated Read Time</span>
                <input
                  name="readTime"
                  value={form.readTime || autoReadTime}
                  onChange={handleInputChange}
                  placeholder="e.g. 6 min"
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#b86ae6] focus:ring-2 focus:ring-[#d59ff5]/50"
                />
              </label>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-semibold text-[#241b37]">Blog Title</span>
              <input
                name="title"
                value={form.title}
                onChange={handleInputChange}
                placeholder="Write a strong, clear blog title"
                className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#b86ae6] focus:ring-2 focus:ring-[#d59ff5]/50"
              />
            </label>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-semibold text-[#241b37]">Short Excerpt</span>
              <textarea
                name="excerpt"
                value={form.excerpt}
                onChange={handleInputChange}
                placeholder="Write a short summary that appears in the blog card"
                rows={3}
                className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#b86ae6] focus:ring-2 focus:ring-[#d59ff5]/50"
              />
            </label>

            <div className="mt-5">
              <span className="mb-2 block text-sm font-semibold text-[#241b37]">Cover Photo</span>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-[26px] border border-dashed border-[#d8c8f1] bg-[#faf7ff] px-4 py-6 text-center transition hover:border-[#b86ae6] hover:bg-white sm:px-6 sm:py-8">
                <UploadCloud className="h-8 w-8 text-[#9a51a0]" />
                <span className="mt-3 text-sm font-semibold text-[#241b37]">
                  {uploadingCover ? 'Uploading Cover...' : 'Upload Cover Photo'}
                </span>
                <span className="mt-1 text-xs text-slate-500">JPG, PNG or WEBP up to 8 MB for blog thumbnail/hero</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverChange} />
              </label>

              {coverName ? (
                <div className="mt-3 flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm">
                  <span className="truncate text-slate-700">{coverName}</span>
                  <button type="button" onClick={clearCover} className="inline-flex items-center gap-1 font-semibold text-[#a03de0]">
                    <X className="h-4 w-4" />
                    Remove
                  </button>
                </div>
              ) : null}
            </div>

            <div className="mt-5 grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-[#241b37]">Author Name</span>
                <input
                  name="authorName"
                  value={form.authorName}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  readOnly
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#b86ae6] focus:ring-2 focus:ring-[#d59ff5]/50"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-[#241b37]">Tags</span>
                <input
                  name="tags"
                  value={form.tags}
                  onChange={handleInputChange}
                  placeholder="engineering, placements, coding"
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#b86ae6] focus:ring-2 focus:ring-[#d59ff5]/50"
                />
              </label>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-semibold text-[#241b37]">Blog Content</span>
              <div className="blog-editor-shell overflow-hidden rounded-[30px] border border-[#ece3f8] bg-[#fffdfb] shadow-[0_20px_40px_rgba(31,23,48,0.06)]">
                <div className="blog-editor-header flex flex-col gap-2 border-b border-[#f0e7fb] px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#9a51a0]">Blog Editor</p>
                    <p className="mt-0.5 text-xs text-slate-500 sm:mt-1 sm:text-sm">Write with headings, lists, quotes, links, and inline emphasis.</p>
                  </div>
                  <span className="self-start rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7d57b1] shadow-sm sm:self-auto">
                    Medium Style
                  </span>
                </div>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.content}
                  onChange={(value) => setForm((current) => ({ ...current, content: value }))}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Tell your story with a strong opening, clear sections, and a memorable takeaway..."
                  className="blog-editor"
                />
              </div>
            </label>

            <div className="mt-5 flex flex-wrap gap-2 sm:mt-6 sm:gap-3">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={saving || loadingDraft}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#7f3df1] via-[#c84caa] to-[#ff7f32] px-5 py-2 text-xs font-semibold text-white shadow-sm transition-transform duration-300 hover:scale-[1.02]"
              >
                <PenSquare className="h-3.5 w-3.5" />
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={handlePreviewPost}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#dfd3f4] bg-white px-5 py-2 text-xs font-semibold text-[#6b4aa1] transition-colors hover:bg-[#faf7ff]"
              >
                <Eye className="h-3.5 w-3.5" />
                Preview Post
              </button>
              <button
                type="button"
                onClick={handlePublishForApproval}
                disabled={saving || loadingDraft}
                className="inline-flex w-full items-center justify-center rounded-xl border border-amber-200 bg-gradient-to-r from-[#fff7e8] to-[#fff1db] px-4 py-2.5 text-sm font-semibold text-[#9a5b00] shadow-sm transition-transform duration-300 hover:scale-[1.02] sm:w-auto sm:rounded-2xl sm:px-6 sm:py-3"
              >
                <Send className="mr-2 h-4 w-4" />
                {saving ? 'Submitting...' : 'Publish for Approval'}
              </button>
            </div>

            <div className={`mt-4 flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium ${statusClasses}`}>
              <CheckCircle2 className="h-4 w-4" />
              <span>{editorStatus}</span>
            </div>
          </div>

          <div className="space-y-4 sm:space-y-6">
            <div className="hidden rounded-[30px] border border-gray-100 bg-white p-6 shadow-sm lg:block">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9a51a0]">Quick Notes</p>
              <ul className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                <li>Choose a clear category so related blogs can be suggested correctly.</li>
                <li>Keep your title specific and reader-friendly.</li>
                <li>Upload a strong cover image because it appears in cards and detail pages.</li>
                <li>Publish sends the post into a pending state so an admin can review and approve it before it goes live.</li>
              </ul>
            </div>

            <div ref={previewRef} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:rounded-[30px] sm:p-6">
              <div className="mb-3 sm:mb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9a51a0]">Live Preview</p>
                <h3 className="mt-1.5 text-lg font-bold text-[#20192d] sm:mt-2 sm:text-2xl">How your blog may look</h3>
              </div>

              <div className="overflow-hidden rounded-[26px] border border-gray-100 bg-white">
                <div className="relative h-52 overflow-hidden bg-[#f4ecff]">
                  {previewCoverSrc ? (
                    <img src={previewCoverSrc} alt="Preview" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#efe6ff] to-[#fdebdc] text-[#9b55dd]">
                      <div className="flex flex-col items-center gap-2">
                        <ImagePlus className="h-10 w-10" />
                        <span className="text-xs font-semibold uppercase tracking-[0.16em]">Cover Preview</span>
                      </div>
                    </div>
                  )}
                  <span className="absolute left-4 top-4 rounded-full bg-gradient-to-r from-[#7f3df1] to-[#ff6f45] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-md">
                    {form.category}
                  </span>
                </div>
                <div className="px-4 pb-4 pt-3.5">
                  <h4 className="text-[1.1rem] font-bold leading-[1.3] text-[#1f1730]">
                    {form.title || 'Your blog title will appear here'}
                  </h4>
                  <p className="mt-2 text-[0.9rem] leading-6 text-[#625470]">
                    {form.excerpt || 'A short excerpt will help readers understand what your blog is about before they open it.'}
                  </p>
                  <div className="mt-3.5 flex items-center justify-between border-t border-gray-100 pt-3">
                    <div>
                      <p className="text-[0.85rem] font-semibold text-[#241b37]">
                        {form.authorName || 'Your Name'}
                      </p>
                      <p className="text-[0.72rem] text-[#807290]">
                        Today | {form.readTime || '5 min'}
                      </p>
                    </div>
                    <span className="text-[0.85rem] font-semibold text-[#a03de0]">Read More</span>
                  </div>
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <p className="mb-3 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#9a51a0]">
                      Content Preview
                    </p>
                    <div
                      className="blog-preview-content text-[0.95rem] leading-7 text-[#4b3e5f]"
                      dangerouslySetInnerHTML={{ __html: previewContent }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {selectedTags.length > 0 ? (
              <div className="rounded-[30px] border border-gray-100 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9a51a0]">Tag Preview</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {selectedTags.map((tag) => (
                    <span key={tag} className="rounded-full bg-[#f4ecff] px-3.5 py-2 text-sm font-semibold text-[#9b55dd]">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WriteBlogPage;
